import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Truck, Shield, Calculator, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';

export default function PricingPage() {
  const { theme } = useTheme();
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="overline mb-4">Transparent Pricing</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="pricing-title">
              Simple, Honest Pricing
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              No hidden fees, no surprises. Our pricing is calculated based on distance and transport type.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Open Carrier */}
            <Card className="glass border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4">
                <Badge variant="secondary">Popular</Badge>
              </div>
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="font-outfit text-2xl">Open Carrier</CardTitle>
                <p className="text-muted-foreground text-sm">
                  Cost-effective transport on an open trailer
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="py-4 border-y border-white/10">
                  <p className="text-sm text-muted-foreground mb-2">Starting from</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-outfit text-4xl font-bold">$1.05</span>
                    <span className="text-muted-foreground">/mile</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">+ $250 base fee</p>
                </div>

                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-accent" />
                    $199 deposit (refundable before dispatch)
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-accent" />
                    Full insurance coverage
                  </li>
                  <li className="flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-accent" />
                    $400 minimum margin
                  </li>
                </ul>

                <Link to="/quote">
                  <Button 
                    variant="outline" 
                    className={`w-full rounded-full border-white/20 ${
                      theme === 'light'
                        ? 'hover:bg-white/10 hover:text-gray-900'
                        : 'hover:bg-white/10'
                    }`}
                    data-testid="pricing-open-cta"
                  >
                    Get Quote
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Enclosed Carrier */}
            <Card className="glass border-accent/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4">
                <Badge className="bg-accent text-accent-foreground">Premium</Badge>
              </div>
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="font-outfit text-2xl">Enclosed Carrier</CardTitle>
                <p className="text-muted-foreground text-sm">
                  Maximum protection in a fully enclosed trailer
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="py-4 border-y border-white/10">
                  <p className="text-sm text-muted-foreground mb-2">Starting from</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-outfit text-4xl font-bold">$1.68</span>
                    <span className="text-muted-foreground">/mile</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">+ $350 base fee</p>
                </div>

                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-accent" />
                    $299 deposit (refundable before dispatch)
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-accent" />
                    Premium insurance coverage
                  </li>
                  <li className="flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-accent" />
                    $700 minimum margin
                  </li>
                </ul>

                <Link to="/quote">
                  <Button 
                    className={`w-full rounded-full ${
                      theme === 'light'
                        ? 'bg-accent text-white hover:bg-accent/90 hover:text-white'
                        : 'bg-accent text-accent-foreground hover:bg-accent/90'
                    }`}
                    data-testid="pricing-enclosed-cta"
                  >
                    Get Quote
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Details */}
          <div className="glass border border-white/10 rounded-2xl p-8">
            <h2 className="font-outfit text-xl font-semibold mb-6">How Pricing Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-medium mb-3">Open Carrier Formula</h3>
                <div className="bg-muted/30 p-4 rounded-lg font-mono text-sm">
                  Price = (Distance × $1.05) + $250
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Example: 1,000 miles = $1,300
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-3">Enclosed Carrier Formula</h3>
                <div className="bg-muted/30 p-4 rounded-lg font-mono text-sm">
                  Price = (Distance × $1.68) + $350
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Example: 1,000 miles = $2,030
                </p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10">
              <h3 className="font-medium mb-4">What's Included</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                <li>Full insurance coverage up to $150,000</li>
                <li>Door-to-door pickup and delivery</li>
                <li>Real-time tracking updates</li>
                <li>Verified and vetted carriers</li>
                <li>Soft tie-down straps for EVs</li>
                <li>No hidden fees or surcharges</li>
              </ul>
              <p className="text-xs text-muted-foreground/70 mt-4">
                Deposit is fully refundable until dispatch confirmation. After dispatch, the deposit is non-refundable. Delivery times are estimates and not guaranteed.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
